import * as z from 'zod';
import { createSelectSchema } from 'drizzle-zod';
import type { QueryResult } from '@iichi/schema-database/types/item';
import * as schema from '@iichi/schema-database';

const itemI18NSchema = createSelectSchema(schema.itemI18N).pick({
  aboutCraftPeriod: true,
  aboutShipping: true,
  craftPeriod: true,
  description: true,
  remark: true,
  title: true,
});

const itemListingFirstLastHistorySchema = createSelectSchema(schema.itemListingFirstLastHistory)
  .pick({
    firstListedAt: true,
    lastListedAt: true,
  })
  .extend({
    firstListedAt: z.coerce.date(),
    lastListedAt: z.coerce.date(),
  });

/**
 * 商品の基本情報スキーマ
 * データベース層のitemSelectSchemaから必要なフィールドのみを選択
 */
export const itemSchema = createSelectSchema(schema.item)
  .pick({
    categoryId: true,
    createdAt: true,
    deleted: true,
    isGift: true,
    isOrdermade: true,
    itemId: true,
    likeCount: true,
    price: true,
    published: true,
    publishedAt: true,
    shopId: true,
    stock: true,
    updatedAt: true,
  })
  .extend({
    createdAt: z.coerce.date(),
    deleted: z.coerce.boolean(),
    isGift: z.coerce.boolean(),
    isOrdermade: z.coerce.boolean(),
    // 型の調整
    published: z.coerce.boolean(),
    publishedAt: z.preprocess((d) => (d === '0000-00-00 00:00:00' ? undefined : d), z.coerce.date().optional()), // '0000-00-00 00:00:00' 対策
    updatedAt: z.coerce.date(),
  })
  .extend({
    aboutCraftPeriod: itemI18NSchema.shape.aboutCraftPeriod,
    aboutShipping: itemI18NSchema.shape.aboutShipping,
    craftPeriod: itemI18NSchema.shape.craftPeriod,
    description: itemI18NSchema.shape.description,
    remark: itemI18NSchema.shape.remark,
    // item_i18nのフィールドを追加
    title: itemI18NSchema.shape.title,
  })
  .extend({
    // item_listing_first_last_historyのフィールドを追加
    firstListedAt: itemListingFirstLastHistorySchema.shape.firstListedAt.optional(),
    lastListedAt: itemListingFirstLastHistorySchema.shape.lastListedAt.optional(),
  });

export type Item = z.infer<typeof itemSchema>;

export const serialize = (data: QueryResult): Item => {
  const item = {
    ...data,
    aboutCraftPeriod: data.itemI18Ns?.[0]?.aboutCraftPeriod ?? '',
    aboutShipping: data.itemI18Ns?.[0]?.aboutShipping ?? '',
    craftPeriod: data.itemI18Ns?.[0]?.craftPeriod ?? '',
    description: data.itemI18Ns?.[0]?.description ?? '',
    firstListedAt: data.itemListingFirstLastHistory?.firstListedAt,
    lastListedAt: data.itemListingFirstLastHistory?.lastListedAt,
    remark: data.itemI18Ns?.[0]?.remark ?? '',
    title: data.itemI18Ns?.[0]?.title ?? '',
  };

  return itemSchema.parse(item);
};
